/**
 * Дверь редактора Раскладки к настоящему серверу (тикет T18).
 *
 * Это единственное место в ядре клиента, которое знает про сеть. Всё остальное —
 * сетка и редактор — работает с портом и про запросы не догадывается (ADR-0002).
 *
 * Ключ сессии сюда не передаётся и здесь не хранится: он лежит в закрытой куке, которую
 * ставит сервер, и уходит сам вместе с запросом. Ядру клиента секрет знать незачем —
 * знать его значит когда-нибудь его обронить.
 */
import { BLOCK_TEXT_SCALES, DEFAULT_BLOCK_VIEW } from './editor.js';
import type { Block, BlockKind, BlockTextScale, BlockView, LayoutGateway } from './editor.js';
import type { Position } from './grid.js';

/**
 * Кто ходит по сети. Отдельным полем, а не прямым обращением к браузеру: так шлюз
 * проверяется без сети, а в настольном приложении подменяется целиком.
 */
export type Fetcher = (url: string, init?: RequestInit) => Promise<Response>;

export interface GatewayOptions {
  /** Куда ходить: адрес сервера без косой черты на конце. */
  readonly baseUrl: string;
  /** Опознаватель Пространства в адресе. */
  readonly spaceSlug: string;
  /**
   * Чья это Раскладка: Канал, заготовка Пространства или заготовка Отдела. Запросы у них
   * одни и те же — различает только начало адреса.
   */
  readonly owner:
    | { readonly kind: 'channel'; readonly channelId: string }
    | { readonly kind: 'space' }
    | { readonly kind: 'department'; readonly departmentId: string };
  /** Язык ответов сервера: названия Типов и объяснения приходят уже переведёнными (§ 11). */
  readonly language?: string;
  readonly fetch?: Fetcher;
}

/** Беда, о которой сервер сказал словами: их и покажет редактор. */
export class GatewayError extends Error {
  constructor(
    message: string,
    /** Код беды с сервера — для оболочки, а не для человека. */
    readonly code: string,
  ) {
    super(message);
    this.name = 'GatewayError';
  }
}

const ownerPath = (options: GatewayOptions): string => {
  const space = `/spaces/${encodeURIComponent(options.spaceSlug)}`;
  switch (options.owner.kind) {
    case 'channel':
      return `${space}/channels/${encodeURIComponent(options.owner.channelId)}/layout`;
    case 'department':
      return `${space}/departments/${encodeURIComponent(options.owner.departmentId)}/layout-template`;
    default:
      return `${space}/layout-template`;
  }
};

/**
 * Разобрать ответ. Сервер объясняет беду словами и на языке смотрящего — их и передаём
 * дальше без переписывания: свой текст поверх серверного стёр бы именно то объяснение,
 * ради которого сервер его и писал.
 */
const parse = async (response: Response): Promise<unknown> => {
  let payload: unknown = null;
  try {
    payload = await response.json();
  } catch {
    payload = null;
  }
  if (response.ok) return payload;

  const said = payload as { error?: unknown; message?: unknown } | null;
  const message = typeof said?.message === 'string' && said.message.trim() ? said.message : null;
  const code = typeof said?.error === 'string' ? said.error : 'unknown';
  if (response.status >= 500) {
    throw new GatewayError('Сервер сейчас не отвечает — попробуйте через минуту', code);
  }
  throw new GatewayError(message ?? 'Сервер не принял правку', code);
};

/** Целое неотрицательное число из ответа; иначе Блок на сетку не поставить. */
const cell = (value: unknown): number => {
  if (typeof value !== 'number' || !Number.isSafeInteger(value) || value < 0) {
    throw new GatewayError('Сервер прислал Раскладку в непонятном виде', 'bad_response');
  }
  return value;
};

const positionOf = (raw: unknown): Position => {
  const source = (raw ?? {}) as Record<string, unknown>;
  return {
    col: cell(source['col']),
    row: cell(source['row']),
    width: cell(source['width']),
    height: cell(source['height']),
  };
};

/**
 * Цвет из ответа. Он уходит прямо в оформление, поэтому всё, что не «#rrggbb», считаем
 * непришедшим: сервер такое и не отдаст, но одна кривая строка из чужого ответа не должна
 * попасть в разметку страницы.
 */
const colourOf = (raw: unknown): string | null =>
  typeof raw === 'string' && /^#[0-9a-f]{6}$/i.test(raw) ? raw : null;

const textScaleOf = (raw: unknown): BlockTextScale | null =>
  BLOCK_TEXT_SCALES.includes(raw as BlockTextScale) ? (raw as BlockTextScale) : null;

/**
 * Личный вид Блока. Старая сборка сервера его не пришлёт — тогда Блок обычный: это
 * честнее отказа, экран соберётся, просто без личных красок.
 */
const viewOf = (raw: unknown): BlockView => {
  if (raw === null || typeof raw !== 'object') return DEFAULT_BLOCK_VIEW;
  const source = raw as Record<string, unknown>;
  return {
    collapsed: source['collapsed'] === true,
    hidden: source['hidden'] === true,
    pinned: source['pinned'] === true,
    background: colourOf(source['background']),
    foreground: colourOf(source['foreground']),
    textScale: textScaleOf(source['textScale']),
  };
};

/**
 * Блок из ответа сервера. Гостю поле «видно Гостю» не приходит вовсе — не потому, что
 * его прячут, а потому, что Гость Раскладку не правит; для него это просто «нет».
 */
const blockOf = (raw: unknown): Block => {
  const source = (raw ?? {}) as Record<string, unknown>;
  const id = source['id'];
  const kind = source['kind'];
  if (typeof id !== 'string' || typeof kind !== 'string') {
    throw new GatewayError('Сервер прислал Раскладку в непонятном виде', 'bad_response');
  }
  return {
    id,
    kind,
    visibleToGuest: source['visibleToGuest'] === true,
    position: positionOf(source['position']),
    view: viewOf(source['view']),
  };
};

const kindOf = (raw: unknown): BlockKind => {
  const source = (raw ?? {}) as Record<string, unknown>;
  const id = source['id'];
  const title = source['title'];
  if (typeof id !== 'string' || typeof title !== 'string') {
    throw new GatewayError('Сервер прислал каталог Блоков в непонятном виде', 'bad_response');
  }
  const size = (source['defaultSize'] ?? {}) as Record<string, unknown>;
  // Наименьший размер сервер шлёт не для всякого Типа. Пришёл — берём: без него редактор
  // разрешил бы сжать Блок до размера, который сервер потом всё равно не примет, и человек
  // узнал бы об отказе уже после того, как отпустил мышь.
  const smallest = source['minimumSize'] as Record<string, unknown> | undefined;
  // Описание Типа пришло не от всякого сервера: старая сборка его не знает. Пустая строка
  // здесь честнее отказа — человек останется без пояснения, но каталог откроется.
  const about = source['about'];
  return {
    id,
    title,
    about: typeof about === 'string' ? about : '',
    defaultSize: { width: cell(size['width']), height: cell(size['height']) },
    ...(smallest
      ? { minimumSize: { width: cell(smallest['width']), height: cell(smallest['height']) } }
      : {}),
  };
};

export function createHttpGateway(options: GatewayOptions): LayoutGateway {
  const go = options.fetch ?? ((url, init) => fetch(url, init));
  const prefix = `${options.baseUrl}${ownerPath(options)}`;
  const withLanguage = (url: string): string =>
    options.language ? `${url}?lang=${encodeURIComponent(options.language)}` : url;

  const request = async (url: string, init?: RequestInit): Promise<unknown> => {
    let response: Response;
    try {
      // Кука сессии уходит только при `include`: без него браузер молча отправит запрос
      // неопознанным, и человек увидит «войдите заново» посреди работы.
      response = await go(url, { credentials: 'include', ...init });
    } catch {
      throw new GatewayError('Нет связи с сервером — проверьте подключение', 'offline');
    }
    return parse(response);
  };

  const send = (url: string, method: string, payload: unknown): Promise<unknown> =>
    request(url, {
      method,
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(payload),
    });

  return {
    async readLayout() {
      const answer = (await request(withLanguage(prefix))) as { blocks?: unknown };
      // Пустая Раскладка приходит пустым списком. Ответ вовсе без списка — это не пустая
      // Раскладка, а непонятный ответ: показать его как пустой экран значило бы соврать,
      // будто Администратор ничего не расставил.
      if (!Array.isArray(answer?.blocks)) {
        throw new GatewayError('Сервер прислал Раскладку в непонятном виде', 'bad_response');
      }
      // Признака личной настройки от старой сборки сервера не придёт — считаем, что её нет:
      // тогда оболочка просто не покажет «вернуть как у всех», и это честно.
      return {
        blocks: answer.blocks.map(blockOf),
        personal: (answer as { personal?: unknown }).personal === true,
      };
    },

    async readKinds() {
      const url = withLanguage(
        `${options.baseUrl}/spaces/${encodeURIComponent(options.spaceSlug)}/block-kinds`,
      );
      const answer = (await request(url)) as { kinds?: unknown };
      if (!Array.isArray(answer?.kinds)) {
        throw new GatewayError('Сервер прислал каталог Блоков в непонятном виде', 'bad_response');
      }
      return answer.kinds.map(kindOf);
    },

    async addBlock({ kind, position }) {
      return blockOf(await send(withLanguage(`${prefix}/blocks`), 'POST', { kind, position }));
    },

    async updateBlock({ blockId, position }) {
      await send(withLanguage(`${prefix}/blocks/${encodeURIComponent(blockId)}`), 'PATCH', {
        position,
      });
    },

    async removeBlock(blockId) {
      await request(withLanguage(`${prefix}/blocks/${encodeURIComponent(blockId)}`), {
        method: 'DELETE',
      });
    },

    // Личные места есть только у Канала: заготовка Пространства и Отдела — болванка для
    // будущих Каналов, смотреть на неё некому, и настраивать её под себя не за чем.
    ...(options.owner.kind === 'channel'
      ? {
          personal: {
            async setPlacement({ blockId, position }) {
              await send(
                withLanguage(`${prefix}/my/blocks/${encodeURIComponent(blockId)}`),
                'PATCH',
                { position },
              );
            },
            async setView({ blockId, view }) {
              const answer = (await send(
                withLanguage(`${prefix}/my/blocks/${encodeURIComponent(blockId)}/view`),
                'PATCH',
                view,
              )) as { view?: unknown };
              return viewOf(answer?.view);
            },
            async clear() {
              await request(withLanguage(`${prefix}/my`), { method: 'DELETE' });
            },
          },
        }
      : {}),
  };
}
